import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { inboxKeys } from '~/lib/query-keys';
import type { FeedResponse } from './use-feed';

/**
 * FRONT-3227: data layer for the tabbed Inbox. Every tab is its own query under
 * `inboxKeys`; New reads the existing feed endpoint with its OWN key, so the old
 * Inbox's `feedKeys` cache and this one never share (or fight over) a response.
 * The api client adds the reader's `x-eddy-timezone` header to every call, which
 * is what the To Do tab's "due today" day boundary is computed from.
 */

export const INBOX_NEW_DAYS = 30;
export const INBOX_NEW_LIMIT = 500;

export type ReviewItem =
  | { kind: 'approval'; id: string; title: string; detail: string | null; sessionId: string | null; createdAt: string }
  | { kind: 'question'; id: string; title: string; detail: string | null; sessionId: string; createdAt: string }
  | { kind: 'plan'; id: string; title: string; detail: string | null; sessionId: string; createdAt: string }
  | { kind: 'learning'; id: string; title: string; detail: string | null; proposalKind: string; createdAt: string }
  | {
      kind: 'task-proposal';
      id: string;
      title: string;
      detail: string | null;
      projectName: string | null;
      dueDate: string | null;
      createdAt: string;
    }
  | { kind: 'knowledge'; id: string; title: string; detail: string | null; itemKind: 'document' | 'upload'; createdAt: string };

export interface ReviewResponse {
  items: ReviewItem[];
  count: number;
}

/** One Done row of a New-tab kind, as `GET /api/inbox/done/markers` sends it (uncapped, last 31 days). */
export interface DoneMarker {
  itemKind: 'cron-run' | 'document' | 'file' | 'operator-run';
  itemId: string;
  handledAt: string;
}

export interface TodoTask {
  taskId: string;
  title: string;
  containerId: string;
  containerName: string;
  dueDate: string;
  overdueDays: number;
}

export interface TodoChat {
  sessionId: string;
  title: string;
  summary: string | null;
  lastActiveAt: string;
}

export interface TodoResponse {
  tasks: TodoTask[];
  chats: TodoChat[];
}

export interface RememberItem {
  id: string;
  proposalKind: string;
  title: string;
  detail: string | null;
  learnedAt: string;
}

export type InboxTab = 'new' | 'todo' | 'review';
export type DoneOutcome =
  | 'done'
  | 'completed'
  | 'approved'
  | 'declined'
  | 'kept'
  | 'discarded'
  | 'task-added'
  | 'still-current';
export type DoneItemKind =
  | 'cron-run'
  | 'document'
  | 'file'
  | 'operator-run'
  | 'chat'
  | 'task'
  | 'approval'
  | 'question'
  | 'learning'
  | 'task-proposal'
  | 'knowledge';

export interface DoneItem {
  id: string;
  itemKind: DoneItemKind;
  itemId: string;
  tab: InboxTab;
  title: string;
  outcome: DoneOutcome;
  handledAt: string;
}

/** New: the existing feed, read through the Inbox's own key. */
export function useInboxNew() {
  return useQuery({
    queryKey: inboxKeys.new(INBOX_NEW_DAYS, INBOX_NEW_LIMIT),
    queryFn: () => api.get<FeedResponse>(`/api/feed?days=${INBOX_NEW_DAYS}&limit=${INBOX_NEW_LIMIT}`),
    // SSE and the Done mutations invalidate this; 30 s spares the 500-item
    // feed a refetch on every tab switch.
    staleTime: 30_000,
    // SSE (`cron-result`, `memory-doc-changed`) invalidates this; the interval
    // is the fallback for a dropped socket, same as the old feed.
    refetchInterval: 5 * 60_000,
  });
}

export function useInboxTodo() {
  return useQuery({
    queryKey: inboxKeys.todo(),
    queryFn: () => api.get<TodoResponse>('/api/inbox/todo'),
  });
}

/** The Review tab's full list. The top bar's number is `useInboxReviewCount`, a lighter request. */
export function useInboxReview() {
  return useQuery({
    queryKey: inboxKeys.review(),
    queryFn: () => api.get<ReviewResponse>('/api/inbox/review'),
    refetchInterval: 60_000,
  });
}

/** The top bar's number only: the server counts without enriching each proposal's entity names. */
export function useInboxReviewCount(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: inboxKeys.reviewCount(),
    queryFn: () => api.get<{ count: number }>('/api/inbox/review/count'),
    enabled: opts.enabled ?? true,
    refetchInterval: 60_000,
  });
}

export function useInboxRemember() {
  return useQuery({
    queryKey: inboxKeys.remember(),
    queryFn: () => api.get<{ items: RememberItem[] }>('/api/inbox/remember'),
  });
}

export function useInboxDone() {
  return useQuery({
    queryKey: inboxKeys.done(),
    queryFn: () => api.get<{ items: DoneItem[] }>('/api/inbox/done'),
  });
}

/** What New hides by. Not `useInboxDone`: that list is capped at 200 and would let old handled items reappear. */
export function useInboxDoneMarkers() {
  return useQuery({
    queryKey: inboxKeys.markers(),
    queryFn: () => api.get<{ markers: DoneMarker[] }>('/api/inbox/done/markers'),
  });
}
